'use client';

/**
 * The dash policy, applied to the DOM — but only to text React has already
 * claimed.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The house rule is "no em dashes on screen" (`lib/ui/dashes.ts`). On the web the
 * text is scattered across ~200 UI files and arrives from the database too (a
 * business name, a menu item description), so the rule has to be enforced on the
 * rendered DOM rather than in the source.
 *
 * The first implementation did that with `public/dash-cleaner.js`, loaded from
 * `app/layout.tsx` with `strategy="afterInteractive"`. It ran while React was
 * still hydrating, and React compares the server's text against the DOM during
 * hydration — so on every page whose copy contains a dash, React found the DOM
 * had already been rewritten and threw:
 *
 *   Hydration failed because the server rendered text didn't match the client.
 *
 * That is not cosmetic. React discards the server HTML for the mismatched
 * subtree and re-renders it on the client, so the guest menu — the highest
 * traffic page in the product — was rendered twice, once on the server for
 * nothing and once on the browser.
 *
 * THE RULE THIS COMPONENT FOLLOWS
 * ───────────────────────────────
 * Only text React has already hydrated may be rewritten. React stamps every host
 * instance it owns with a per-root `__reactFiber$…` key, and it sets that key
 * when it commits the instance — which is after hydration has compared the text.
 * So:
 *
 *   - Text under a React-owned element → React has compared it → safe to rewrite.
 *   - Text under an element React has not claimed yet → React has NOT compared
 *     it → rewriting it would CAUSE the mismatch we are here to remove.
 *
 * Text that arrives later (a streamed Suspense boundary, a toast, a status
 * update) is picked up by the observer and scrubbed once React has claimed it.
 * Until then it keeps its dash, which is the correct trade: a visible dash for a
 * moment is strictly better than a broken hydration for the whole page.
 */

import { useEffect } from 'react';

import { hasDash, stripDashes } from '@/lib/ui/dashes';

/** React's per-root marker for a host instance it owns. */
const REACT_FIBER_PREFIX = '__reactFiber$';

/**
 * The `__reactFiber$…` key names seen so far. React uses one random suffix per
 * root; the suffix is discovered from the first owned element and then reused,
 * so the hot path is a lookup rather than a scan of every key on every element.
 */
const reactFiberKeys = new Set<string>();

/** Has React claimed this element? */
function reactOwns(element: Element | null): boolean {
  if (!element) return false;
  for (const key of reactFiberKeys) {
    if ((element as unknown as Record<string, unknown>)[key] !== undefined) return true;
  }
  for (const key of Object.keys(element)) {
    if (key.startsWith(REACT_FIBER_PREFIX)) {
      reactFiberKeys.add(key);
      return true;
    }
  }
  return false;
}

/**
 * Rewrite one text node if the policy applies to it.
 *
 * Returns true when the node could NOT be scrubbed because React has not claimed
 * its parent yet — the caller keeps it on a retry list.
 */
function scrubTextNode(node: Text): boolean {
  const value = node.nodeValue;
  if (!value || !hasDash(value)) return false;
  if (!reactOwns(node.parentElement)) return true;
  const next = stripDashes(value);
  if (next !== value) node.nodeValue = next;
  return false;
}

/**
 * Scrub every text node under `root`.
 *
 * Returns true when some text was left alone because React has not claimed it
 * yet, so the caller can come back for it.
 */
function scrubSubtree(root: Node): boolean {
  let pending = false;
  if (root.nodeType === Node.TEXT_NODE) {
    pending = scrubTextNode(root as Text);
  } else {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      if (scrubTextNode(node as Text)) pending = true;
      node = walker.nextNode();
    }
  }
  return pending;
}

/**
 * How long a text node may wait for React to claim it before we give up on it.
 *
 * A streamed boundary is claimed within a frame or two. Anything still unclaimed
 * after this window belongs to something React will never claim (a third-party
 * script, a browser extension), so the retry loop stops rather than spinning for
 * the life of the page.
 */
const MAX_PENDING_FRAMES = 120;

export function DashCleaner() {
  useEffect(() => {
    /** Elements whose text is waiting for React to claim it, and when we first saw them. */
    const pending = new Map<Element, number>();
    let frame = 0;
    let frameCount = 0;

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        frameCount += 1;
        for (const [element, firstSeen] of Array.from(pending)) {
          if (!element.isConnected || frameCount - firstSeen > MAX_PENDING_FRAMES) {
            pending.delete(element);
            continue;
          }
          if (!scrubSubtree(element)) pending.delete(element);
        }
        if (pending.size) schedule();
      });
    };

    /** Scrub a subtree, remembering it if React has not claimed it yet. */
    const scrub = (root: Node) => {
      if (!scrubSubtree(root)) return;
      const element =
        root.nodeType === Node.TEXT_NODE ? root.parentElement : (root as Element);
      if (!element) return;
      if (!pending.has(element)) pending.set(element, frameCount);
      schedule();
    };

    // The shell has hydrated by the time this effect runs: React commits the
    // tree, then flushes effects, so every element it owns is already claimed.
    if (document.body) scrub(document.body);

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') {
          scrub(record.target);
          continue;
        }
        for (const node of record.addedNodes) scrub(node);
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return null;
}
